import { useMemo, useState } from 'react';

// Third-party
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid2';
import Typography from '@mui/material/Typography';
import CardHeader from '@mui/material/CardHeader';
import CardContent from '@mui/material/CardContent';
import startCase from 'lodash/startCase';
import Box from '@mui/material/Box';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import Avatar from '@mui/material/Avatar';
import Card from '@mui/material/Card';
import { useTheme } from '@mui/material';

// Components
import LoadingButton from '@mui/lab/LoadingButton';
import { TCustomTheme } from '@mgm/common/theme';
import { getAvatarReader, BASE_COLORS, generateURL } from '@mgm/common/utils';
import { useCustomTheme } from '@mgm/common/hooks/useCustomTheme';
import { MemoizedBaseAvatar, BaseLabel } from '@mgm/common/components/base';
import { CustomTextField } from '@mgm/common/components/form';
import { useAvatarFile, useShortName } from '@mgm/common/hooks/usePersonnel';
import { SettingsHeader } from './SettingsHeader';
import { SettingsWrapper } from '../../../layout';
import { PhoneField, TCreateEditForm } from '../../../components/form';
import { DropzoneFile } from '../../../components/files';

// Hooks
import { useAppSelector } from '../../../hooks/useAppStore';
import { useAvatarFileAPI } from '../../../hooks/api/useStorage';

// Redux-toolkit
import { getAuthenticateUser, getTokenDetails } from '../../../store/authenticate/selectors';

// Utils
import { useUpdateUserAPI } from '../../../hooks/api';
import { LazyAvatar } from '../../../components';

const ProfileSettings = () => {
  const { mutateAsync: avatarMutate, isLoading: isAvatarLoading } = useAvatarFileAPI();
  const { mutateAsync: updateUserMutate, isLoading } = useUpdateUserAPI();
  const theme = useTheme() as TCustomTheme;
  const { isMobile } = useCustomTheme(theme);
  const { personnel: userDetails } = useAppSelector(getAuthenticateUser);
  const tokenDetails = useAppSelector(getTokenDetails);
  const { t } = useTranslation(['inputs', 'settings']);
  const { avatarSrc } = useAvatarFile(userDetails?.avatarFile);
  const { shortName: avatarShortName } = useShortName(userDetails?.name);

  const methods = useForm<Omit<TCreateEditForm, 'commissionPlan'>>({
    mode: 'all',
    values: {
      firstName: userDetails?.name.split(' ')[0],
      lastName: userDetails?.name.split(' ')[1],
      phone: userDetails?.phone?.e164 || '',
    },
  });

  const [avatarFile, setAvatarFile] = useState({} as File);

  const newAvatarFile = useMemo(() => {
    let avatar = avatarSrc;

    if (avatarFile?.name) {
      avatar = generateURL(avatarFile);
    }
    return avatar;
  }, [avatarFile]);

  const saveAvatarFile = () => {
    const reader = getAvatarReader(avatarFile);

    reader.onload = async (e) => {
      await avatarMutate(e.target?.result);
      setAvatarFile({} as File);
    };
  };

  const saveUserChanges = async (data: Omit<TCreateEditForm, 'commissionPlan'>) => {
    const payload = {
      settings: {
        ...tokenDetails.settings,
      },
      name: `${data.firstName} ${data.lastName}`,
      phone: data.phone,
    };

    await updateUserMutate(payload);
  };

  return (
    <SettingsWrapper>
      <SettingsHeader
        title={t('personalInfo', { ns: 'settings' })}
        subtitle={t('updateYourPhoto', { ns: 'settings' })}
      />
      <Divider sx={{ pt: 4, borderStyle: 'dashed' }} />
      <Grid
        sx={{
          pt: 4,
          pb: 8,
          paddingTop: { xs: 2 },
        }}
      >
        <Grid alignItems="center" display="flex">
          <MemoizedBaseAvatar
            size={{
              width: 100,
              height: 100,
            }}
            fontSize={30}
            avatarFile={userDetails?.avatarFile}
            avatarName={userDetails?.name}
          />
          <Box>
            <Typography sx={{ ml: 2 }} variant="h3">{startCase(userDetails.name)}</Typography>
            <Typography
              sx={{ ml: 2 }}
              fontSize={16}
              color={theme.palette.primary.main}
            >
              {startCase(userDetails?.position)}
            </Typography>
          </Box>
        </Grid>
        <Grid spacing={2} columns={12} container>
          <Grid size={{ sm: 12, md: 6, lg: 6 }}>
            <Card
              sx={{
                mt: 6,
                marginTop: { xs: 2 },
                borderRadius: 2,
                ':hover': {
                  boxShadow: 'rgba(32, 40, 45, 0.08) 0px 2px 14px 0px',
                },
              }}
            >
              <CardHeader
                sx={{
                  p: 2,
                }}
                title={t('editAccountDetails', { ns: 'settings' })}
                titleTypographyProps={{
                  variant: 'subtitle1',
                }}
              />
              <Divider sx={{ borderStyle: 'dashed' }} />
              <CardContent>
                <Grid>
                  <FormProvider {...methods}>
                    <Grid spacing={2} columns={12} container>
                      <Grid size={{ xs: 12, lg: 6 }}>
                        <CustomTextField
                          placeholder={t('enterFirstName')}
                          labelText={t('firstName')}
                          name="firstName"
                        />
                      </Grid>
                      <Grid size={{ xs: 12, lg: 6 }}>
                        <CustomTextField
                          placeholder={t('enterLastName')}
                          labelText={t('lastName')}
                          name="lastName"
                        />
                      </Grid>
                    </Grid>
                    <Grid columns={12} container>
                      <Grid size={{ xs: 12, lg: 12 }} paddingTop={2}>
                        <BaseLabel letterSpacing="-0.5px">{t('contactInfo')}</BaseLabel>
                        <PhoneField />
                      </Grid>
                    </Grid>
                  </FormProvider>
                </Grid>
                <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                  <LoadingButton
                    onClick={methods.handleSubmit(saveUserChanges)}
                    loading={isLoading}
                    disabled={!methods.formState.isValid || !Object.keys(methods.formState.dirtyFields).length}
                    type="submit"
                    variant="contained"
                  >
                    {t('saveChanges', { ns: 'settings' })}
                  </LoadingButton>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ sm: 12, lg: 6, md: 6 }}>
            <Card
              sx={{
                ':hover': {
                  boxShadow: 'rgba(32, 40, 45, 0.08) 0px 2px 14px 0px',
                },
                mt: 6,
                marginTop: { xs: 2 },
                borderRadius: 2,
              }}
            >
              <CardHeader
                sx={{
                  p: 2,
                }}
                title={t('profilePicture', { ns: 'settings' })}
                titleTypographyProps={{
                  variant: 'subtitle1',
                }}
              />
              <Divider sx={{ borderStyle: 'dashed' }} />
              <CardContent>
                <Grid columns={12} container>
                  <Grid
                    justifyContent="center"
                    alignItems="center"
                    pb={isMobile ? 3 : 0}
                    display="flex"
                    size={{ sm: 2, lg: 2, md: 2 }}
                  >
                    {newAvatarFile ? (
                      <LazyAvatar
                        sx={{
                          width: 60,
                          height: 60,
                        }}
                        src={newAvatarFile}
                        alt="new avatar"
                      />
                    ) : (
                      <Avatar
                        sx={{
                          bgcolor: BASE_COLORS.avatarBG,
                        }}
                      >
                        <div className="avatar-text">
                          {avatarShortName}
                        </div>
                      </Avatar>
                    )}
                  </Grid>
                  <Grid size={{ sm: 10, md: 10, lg: 10 }}>
                    <DropzoneFile
                      avatarFile={avatarFile}
                      setAvatarFile={setAvatarFile}
                    />
                  </Grid>
                </Grid>
                <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                  <LoadingButton
                    onClick={saveAvatarFile}
                    disabled={!avatarFile.name}
                    loading={isAvatarLoading}
                    type="submit"
                    variant="contained"
                  >
                    {t('saveChanges', { ns: 'settings' })}
                  </LoadingButton>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Grid>
    </SettingsWrapper>
  );
};

export {
  ProfileSettings,
};
